"use client"

import { useEffect, useRef, useState } from "react"
import { useTranslations } from "next-intl"
import { ChevronDown } from "@/lib/icons"

/** First visit on a phone: the app opens with just the welcome panel and this hint at the top. Pulling down
 * anywhere (or tapping the pill) reveals the toolbar. */
export function PullDownHint({ onReveal }: { onReveal: () => void }) {
  const t = useTranslations("firstVisitIntro")
  const startY = useRef<number | null>(null)

  useEffect(() => {
    const onStart = (e: TouchEvent) => {
      startY.current = e.touches[0]?.clientY ?? null
    }
    const onMove = (e: TouchEvent) => {
      if (startY.current === null) return
      const dy = (e.touches[0]?.clientY ?? 0) - startY.current
      // A downward pull from the top of the page.
      if (dy > 48 && window.scrollY <= 0) {
        startY.current = null
        onReveal()
      }
    }
    window.addEventListener("touchstart", onStart, { passive: true })
    window.addEventListener("touchmove", onMove, { passive: true })
    return () => {
      window.removeEventListener("touchstart", onStart)
      window.removeEventListener("touchmove", onMove)
    }
  }, [onReveal])

  return (
    <div className="pointer-events-none fixed inset-x-0 top-0 z-[60] flex justify-center px-4 pt-3">
      <div
        role="button"
        tabIndex={0}
        onClick={onReveal}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onReveal()}
        className="pointer-events-auto flex cursor-pointer items-center gap-3 rounded-full bg-[#6d4aff] py-2 pl-4 pr-5 text-white shadow-[0_8px_24px_rgba(109,74,255,0.45)]"
      >
        <span className="flex flex-col items-center" aria-hidden="true">
          {[0, 1].map((i) => (
            <ChevronDown
              key={i}
              className={"h-4 w-4 " + (i === 0 ? "" : "-mt-2")}
              style={{ animation: `intro-chevron 1.3s ease-in-out ${i * 0.2}s infinite` }}
            />
          ))}
        </span>
        <span className="text-left">
          <span className="block text-[13px] font-bold leading-tight">{t("pullDown")}</span>
          <span className="block text-[11px] leading-tight text-white/80">{t("pullDownSub")}</span>
        </span>
      </div>
    </div>
  )
}

/** Points at the Filters button with a short label. Sits under the button, clamped inside the screen. */
export function FiltersBubble({ targetId, onDismiss }: { targetId: string; onDismiss: () => void }) {
  const t = useTranslations("firstVisitIntro")
  const [pos, setPos] = useState<{ left: number; top: number; arrow: number } | null>(null)
  const WIDTH = 216

  useEffect(() => {
    const place = () => {
      const el = document.getElementById(targetId)
      if (!el) return
      const r = el.getBoundingClientRect()
      const center = r.left + r.width / 2
      const left = Math.max(8, Math.min(window.innerWidth - WIDTH - 8, center - WIDTH / 2))
      setPos({ left, top: r.bottom + 12, arrow: Math.max(14, Math.min(WIDTH - 14, center - left)) })
    }
    // The toolbar slides down for about half a second (longer on a slow device), so keep re-measuring while it settles.
    const track = window.setInterval(place, 120)
    const stopTracking = window.setTimeout(() => window.clearInterval(track), 2000)
    window.addEventListener("resize", place)
    const timer = window.setTimeout(onDismiss, 9000)
    const tap = () => onDismiss()
    // Any tap dismisses it (a tap on the Filters button itself still opens Filters as usual).
    document.addEventListener("pointerdown", tap, { once: true })
    return () => {
      window.removeEventListener("resize", place)
      window.clearTimeout(timer)
      window.clearInterval(track)
      window.clearTimeout(stopTracking)
      document.removeEventListener("pointerdown", tap)
    }
  }, [targetId, onDismiss])

  if (!pos) return null
  return (
    <div
      role="status"
      className="pointer-events-none fixed z-[60] rounded-2xl border border-[#6d4aff]/50 bg-white px-3.5 py-2.5 text-[#1c1a24] shadow-[0_8px_28px_rgba(30,20,60,0.22)]"
      style={{ left: pos.left, top: pos.top, width: WIDTH, animation: "intro-bubble-in 0.35s ease-out both" }}
    >
      <span
        aria-hidden="true"
        className="absolute -top-[7px] h-3 w-3 rotate-45 border-l border-t border-[#6d4aff]/50 bg-white"
        style={{ left: pos.arrow - 6 }}
      />
      <p className="text-[13px] font-bold">{t("filtersTitle")}</p>
      <p className="mt-0.5 text-xs text-[#6b6779]">{t("filtersSub")}</p>
    </div>
  )
}
